import { onSessionReset } from "./session";
// What a refresh should not lose that the address does not say: how far each pane was
// scrolled, the row picked out in each list, where the open article was being read. Kept for
// this tab only (sessionStorage), written as the page goes away, read once as it comes back.
import { useLayoutEffect, useRef } from "react";

const KEY = "vladrss.kept";
type Kept = Record<string, {
  top: number;
  selected?: string | null;
  /** the row at the top of a list, and how far it was scrolled past */ anchor?: string; offset?: number;
  /** the article being read, and the paragraph under the reading line and how far into it */ id?: string; block?: number; within?: number;
}>;

const kept: Kept = (() => {
  try { return JSON.parse(sessionStorage.getItem(KEY) ?? "{}") as Kept; } catch { return {}; }
})();

export const keptAt = (key: string) => kept[key];
export const keep = (key: string, value: Kept[string]) => {
  delete kept[key]; kept[key] = value;
  for (const old of Object.keys(kept).slice(0, -40)) delete kept[old];
};
onSessionReset(() => { for (const key of Object.keys(kept)) delete kept[key]; try { sessionStorage.removeItem(KEY); } catch { /* storage may be unavailable */ } });

/** Things to note down just as the page goes away (measuring them on every scroll would cost). */
const leaving = new Set<() => void>();
export function onLeave(note: () => void) { leaving.add(note); return () => { leaving.delete(note); }; }
/** Notes them now: the page is about to show something else (another list), not to go away. */
export const noteNow = () => leaving.forEach((note) => note());

if (typeof window !== "undefined") {
  const save = () => { leaving.forEach((note) => note()); try { sessionStorage.setItem(KEY, JSON.stringify(kept)); } catch { /* fine: a refresh starts fresh */ } };
  addEventListener("pagehide", save);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") save(); });
}

/**
 * A pane that comes back scrolled where it was: give the scrolling element `ref` and `onScroll`.
 * `key` names what it shows (a Settings section), so each keeps its own place.
 */
export function useKeptScroll<T extends HTMLElement>(key: string) {
  const ref = useRef<T>(null);
  const placing = useRef<Placing | null>(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const p = placeAt(ref.current, keptAt(key)?.top ?? 0);
    placing.current = p;
    return p.stop;
  }, [key]);
  const onScroll = () => {
    const el = ref.current;
    if (el && !placing.current?.mine(el.scrollTop)) keep(key, { top: el.scrollTop });
  };
  return { ref, onScroll };
}

export interface Placing { /** whether a scroll position is the one put back (not the reader's) */ mine: (top: number) => boolean; stop: () => void }

/**
 * Puts a pane back at `top`. What fills it may still be arriving (rows, a list of sources), so
 * it follows the content until the place is there, a moment at most, unless the reader scrolls first.
 */
export function placeAt(el: HTMLElement, top: number): Placing {
  let placed: number | null = null;
  let ro: ResizeObserver | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stop = () => { ro?.disconnect(); clearTimeout(timer); };
  const place = () => { el.scrollTop = top; placed = el.scrollTop; return placed >= top - 1; };
  if (!place()) {
    ro = new ResizeObserver(() => { if (placed === null || place()) stop(); });
    for (const c of Array.from(el.children)) ro.observe(c);
    timer = setTimeout(stop, 2500);
  }
  return {
    mine: (y) => {
      if (placed !== null && Math.abs(y - placed) < 1) return true;
      placed = null; stop();
      return false;
    },
    stop,
  };
}
